/**
 * Account Context
 *
 * Provides the list of connected ad accounts and the currently selected account
 * across the entire authenticated app.
 *
 * Persistence:
 *   - The selected account's external_account_id is stored in localStorage under
 *     the key `adflow_selected_account`.
 *   - On next load the stored ID is matched against the fresh account list.
 *     If the stored account no longer exists (disconnected), the first available
 *     account is selected instead.
 *
 * Empty state:
 *   - When the user has no connected accounts, selectedAccount is null and
 *     hasAccounts is false.  Components should show a "connect your first account"
 *     prompt in that case.
 *
 * Usage:
 *   import { useAccount } from '../context/AccountContext';
 *   const { selectedAccount, accounts, selectAccount, hasAccounts, isLoading } = useAccount();
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useAuth } from './AuthContext';
import { getConnectedAccounts } from '../services/accountsService';
import { getClientAccounts } from '../services/orgService';
import OrgContext from './OrgContext';

const STORAGE_KEY = 'adflow_selected_account';

const AccountContext = createContext(null);

export function AccountProvider({ children }) {
  const { isAuthenticated } = useAuth();
  // Read OrgContext directly — may be null if OrgProvider is not an ancestor
  const orgCtx = useContext(OrgContext);
  const selectedOrg = orgCtx?.selectedOrg ?? null;
  const selectedClient = orgCtx?.selectedClient ?? null;
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load accounts — when a client is selected, fetch client-specific accounts;
  // otherwise fall back to the user's global connected accounts.
  useEffect(() => {
    if (!isAuthenticated) {
      setAccounts([]);
      setSelectedAccount(null);
      return;
    }

    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError(null);
      try {
        let data;
        if (selectedOrg?.id && selectedClient?.id) {
          // Fetch accounts linked to the selected client
          const raw = await getClientAccounts(selectedOrg.id, selectedClient.id);
          // Normalize LinkedAccountRow → same shape as getConnectedAccounts
          data = (Array.isArray(raw) ? raw : []).map((a) => ({
            ...a,
            provider: a.provider || a.channel_type,
            external_account_id: a.external_account_id || a.channel_account_id,
            account_name: a.account_name || a.channel_account_name || a.channel_account_id,
          }));
        } else {
          // No client selected — show all user accounts
          data = await getConnectedAccounts();
        }
        if (cancelled) return;

        const activeAccounts = Array.isArray(data) ? data.filter((a) => a.is_active !== false) : [];
        setAccounts(activeAccounts);

        // Restore persisted selection, falling back to the first account
        const storedId = localStorage.getItem(STORAGE_KEY);
        const restored = storedId
          ? activeAccounts.find((a) => a.external_account_id === storedId)
          : null;

        setSelectedAccount(restored ?? activeAccounts[0] ?? null);
      } catch (err) {
        if (!cancelled) {
          setError(err);
          setAccounts([]);
          setSelectedAccount(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [isAuthenticated, selectedOrg?.id, selectedClient?.id]);

  const selectAccount = useCallback((account) => {
    setSelectedAccount(account);
    if (account?.external_account_id) {
      localStorage.setItem(STORAGE_KEY, account.external_account_id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const value = {
    accounts,
    selectedAccount,
    selectAccount,
    hasAccounts: accounts.length > 0,
    isLoading,
    error,
    // Convenience: the ID string most API calls need
    selectedAccountId: selectedAccount?.external_account_id ?? null,
  };

  return (
    <AccountContext.Provider value={value}>
      {children}
    </AccountContext.Provider>
  );
}

export function useAccount() {
  const ctx = useContext(AccountContext);
  if (!ctx) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return ctx;
}

export default AccountContext;
